import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Trash2, Check, X, Search } from 'lucide-react';
import dayjs from 'dayjs';
import Alert from '../../../components/Alert'

interface ChatItem {
    conversation_id: string;
    preview: string;
    last_updated: string;
    source?: 'chat' | 'ledger_agent';
}

interface SidePanelProps {
    chats: ChatItem[];
    onSelect: (conversation_id: string) => void;
    selectedId: string | null;
    handleShowModal: (conversation_id: string, title: string) => void;
    onEdit: (conversation_id: string, newTitle: string) => void;
    searchQuery: string;
    onSearchChange: (value: string) => void;
    readOnly?: boolean;
}

const SidePanel: React.FC<SidePanelProps> = ({
    chats,
    onSelect,
    selectedId,
    handleShowModal,
    onEdit,
    searchQuery,
    onSearchChange,
    readOnly = false,
}) => {
    const formatPreviewLabel = (value: string) => {
        const text = (value || '').trim();
        if (!text) return '';

        const lettersOnly = text.replace(/[^A-Za-z]+/g, '');
        const isAllCaps = lettersOnly.length >= 6 && lettersOnly === lettersOnly.toUpperCase();
        if (!isAllCaps) {
            return text;
        }

        return text
            .toLowerCase()
            .replace(/\b\w/g, (match) => match.toUpperCase());
    };

    const formRef = useRef<HTMLFormElement | null>(null);
    const [showEdit, setShowEdit] = useState(false);
    const [editTarget, setEditTarget] = useState('');
    const [input, setInput] = useState('');
    const groupChatsByDate = (chats: ChatItem[]) => {
        const grouped: { [key: string]: ChatItem[] } = {
            "Today": [],
            "Yesterday": [],
            "Previous 7 days": [],
            "Previous 30 days": [],
            "Older": [],
        };

        const now = dayjs();

        chats.forEach(chat => {
            const updated = dayjs(chat.last_updated);
            const diffDays = now.diff(updated, 'day');

            if (diffDays === 0) {
                grouped["Today"].push(chat);
            } else if (diffDays === 1) {
                grouped["Yesterday"].push(chat);
            } else if (diffDays <= 7) {
                grouped["Previous 7 days"].push(chat);
            } else if (diffDays <= 30) {
                grouped["Previous 30 days"].push(chat);
            } else {
                grouped["Older"].push(chat);
            }
        });

        return grouped;
    };

    const groupedChats = groupChatsByDate(chats);

    // Handles clicking outside while editing
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (formRef.current && !formRef.current.contains(event.target as Node)) {
                setShowEdit(false);
                setEditTarget('');
            }
        };

        if (showEdit) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEdit]);

    const formatTimestamp = (timestamp: string) => {
        const value = dayjs(timestamp);
        if (!value.isValid()) return '';
        return value.format('MMM D, YYYY h:mm A');
    };

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden font-normal text-gray-800">
            <div className="shrink-0 px-4 pt-4">
                <div className="flex h-[36px] w-full max-w-[272px] items-center gap-4 rounded-md border border-gray-200 bg-white px-3 py-1.5">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search Chat History"
                        className="w-full bg-transparent text-sm text-gray-700 placeholder:text-gray-400 outline-none"
                    />
                    <Search className="w-4 h-4 text-gray-400 shrink-0" />
                </div>
            </div>

            {/* <button className="flex font-medium items-center gap-2 px-4"
                onClick={() => handleNewChat()}>
                <SquarePen />
                <div className="text-left">Create new chat</div>
            </button> */}

            {chats.length === 0 ?
                <div className="flex min-h-0 flex-1 items-center justify-center">
                    <Alert />
                </div> :
                <ul className="mt-5 min-h-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden pb-4">
                    {Object.entries(groupedChats).map(([section, items]) => (
                        items.length > 0 && (
                            <li key={section}>
                                {/* Grouped by Date */}
                                <p className="text-gray-500 font-medium px-4 mb-1 mt-5">{section}</p>
                                <ul className="space-y-2">
                                    {items.map(chat => (
                                        <li key={chat.conversation_id} onClick={() => onSelect(chat.conversation_id)}>
                                            {showEdit && editTarget === chat.conversation_id ?
                                                // Edit Chat
                                                (
                                                    <form
                                                        ref={formRef}
                                                        onSubmit={(e) => {
                                                            e.preventDefault();
                                                            onEdit(chat.conversation_id, input);
                                                            setShowEdit(false);
                                                            setEditTarget('');
                                                        }}
                                                        className="flex items-center justify-between w-full bg-gray-100 p-1"
                                                    >
                                                        <Pencil className="shrink-0 w-4 h-4" />
                                                        <input
                                                            value={input}
                                                            onChange={(e) => setInput(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="bg-transparent p-1 w-full mx-2"
                                                        />
                                                        <div className="flex items-center space-x-2">
                                                            <button type="submit"><Check className="w-4 h-4" /></button>
                                                            <button onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditTarget('');
                                                                setShowEdit(false);
                                                            }}><X className="w-4 h-4" /></button>
                                                        </div>
                                                    </form>
                                                ) : (
                                                    // Default Preview
                                                    <div
                                                        className={`group cursor-pointer flex items-center justify-between p-1 px-4 hover:bg-gray-100 ${chat.conversation_id === selectedId ? 'bg-gray-200 font-medium' : ''
                                                            }`}
                                                    >
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <div className="truncate">{formatPreviewLabel(chat.preview)}</div>
                                                                {/* {chat.source === 'ledger_agent' && (
                                                                    <span className="shrink-0 rounded-full bg-amber-100 px-1.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                                                                        Agent
                                                                    </span>
                                                                )} */}
                                                            </div>
                                                            <div className="mt-1 text-xs font-normal text-gray-500">
                                                                {formatTimestamp(chat.last_updated)}
                                                            </div>
                                                        </div>
                                                        {!readOnly && chat.source !== 'ledger_agent' && (
                                                            <div className={`${chat.conversation_id === selectedId && '!flex opacity-100'} 
                                                    lg:flex hidden m-1 items-center space-x-2 opacity-0 group-hover:opacity-100 transition`}>

                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setShowEdit(true);
                                                                        setEditTarget(chat.conversation_id);
                                                                        setInput(chat.preview);
                                                                    }}
                                                                    className="hover:text-gray-500"
                                                                >
                                                                    <Pencil className="icon-1" />
                                                                </button>

                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleShowModal(chat.conversation_id, chat.preview);
                                                                    }}
                                                                    className="hover:text-gray-500"
                                                                >
                                                                    <Trash2 className="icon-1" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        )
                    ))}
                </ul>}

        </div>
    );
};

export default SidePanel;
